"use client";

import { useState, useMemo, useCallback } from "react";
import { useSearchParams, usePathname } from "next/navigation";
import { useTranslations, useLocale } from "next-intl";
import RealProductCard from "@/components/RealProductCard";
import {
  SearchFilterBar,
  SortDialog,
  type SortOption,
} from "@/components/search";
import CategoryFilterDialog from "@/components/CategoryFilterDialog";
import { type UsageMethod } from "@/components/UsageMethodFilter";
import { useProductAvailability } from "@/hooks/useProductAvailability";
import { CATEGORIES, getCategoryName } from "@/config/categories";
import { parseFutureDateParam } from "@/lib/utils/date-filter";
import { cn } from "@/lib/utils";
import type { Product, ProductCategory } from "@/types";
import type { Locale } from "@/i18n/routing";

// Quick chips over the concert grid: lightsticks (K-pop·공연) and smartphones.
const QUICK_CATEGORY_CHIPS = [
  { code: "10-06", labelKey: "venue.chipLightstick" },
  { code: "20-01", labelKey: "venue.chipPhone" },
] as const;

function isInCategory(code: string | null | undefined, root: string) {
  return !!code && (code === root || code.startsWith(`${root}-`));
}

interface VenueProductsClientProps {
  products: Product[];
  heading: string;
  noResultsText: string;
}

/**
 * Client-side filter UI (date / filter / sort) for the venue nearby-product
 * list — same 3-button bar as the promotion pages. The incoming `products`
 * order is the server's distance + priority-zone ranking, so unlike the
 * promotion page the default sort is "no sort" (null): the server order is
 * kept until the user explicitly picks a sort option.
 */
export default function VenueProductsClient({
  products,
  heading,
  noResultsText,
}: VenueProductsClientProps) {
  const tSearch = useTranslations("search");
  const tConcert = useTranslations("concert");
  const locale = useLocale();
  const localeKey = locale as Locale;
  const searchParams = useSearchParams();
  const pathname = usePathname();

  // 날짜 필터 (URL: checkin, checkout) — 과거 날짜는 가드로 필터링
  const [checkinDate, setCheckinDate] = useState<string | null>(() =>
    parseFutureDateParam(searchParams.get("checkin")),
  );
  const [checkoutDate, setCheckoutDate] = useState<string | null>(() =>
    parseFutureDateParam(searchParams.get("checkout")),
  );

  // 필터 다이얼로그 (카테고리/이용방식/가격)
  const [isFilterDialogOpen, setIsFilterDialogOpen] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>(() => {
    const param = searchParams.get("categories");
    return param ? param.split(",").filter(Boolean) : [];
  });
  const [usageMethod, setUsageMethod] = useState<UsageMethod>(() => {
    const param = searchParams.get("usage_method");
    if (param && ["all", "pickup", "delivery", "shipping"].includes(param)) {
      return param as UsageMethod;
    }
    return "all";
  });
  const [priceRange, setPriceRange] = useState<[number, number] | null>(() => {
    const minP = searchParams.get("min_price");
    const maxP = searchParams.get("max_price");
    if (minP && maxP) return [parseInt(minP, 10), parseInt(maxP, 10)];
    return null;
  });

  // 정렬 (URL: sort) — null이면 서버 순서(거리+priority) 유지
  const [isSortDialogOpen, setIsSortDialogOpen] = useState(false);
  const [sortBy, setSortBy] = useState<SortOption | null>(() => {
    const s = searchParams.get("sort");
    if (s && ["price_low", "price_high", "newest"].includes(s)) {
      return s as SortOption;
    }
    return null;
  });

  // 카테고리 리스트 (로컬라이즈)
  const allCategories = useMemo<ProductCategory[]>(
    () =>
      CATEGORIES.map((cat) => ({
        code: cat.code,
        name: getCategoryName(cat, localeKey),
        level: cat.level,
        parent_code: cat.parent_code,
      })),
    [localeKey],
  );

  // 1) 카테고리 필터 (부모 순회)
  const filteredByCategory = useMemo(() => {
    if (selectedCategories.length === 0) return products;
    if (allCategories.length === 0) return products;

    return products.filter((product) => {
      if (!product.category_code) return false;
      if (selectedCategories.includes(product.category_code)) return true;
      // Prefix match keeps subcategories (10-06-01 …) in even when the local
      // category config lags behind the database.
      if (
        selectedCategories.some((code) =>
          isInCategory(product.category_code, code),
        )
      ) {
        return true;
      }

      const productCategory = allCategories.find(
        (c) => c.code === product.category_code,
      );
      if (!productCategory) return false;

      let current = productCategory;
      while (current.parent_code) {
        if (selectedCategories.includes(current.parent_code)) return true;
        const parent = allCategories.find(
          (c) => c.code === current.parent_code,
        );
        if (!parent) break;
        current = parent;
      }
      return false;
    });
  }, [products, selectedCategories, allCategories]);

  // 2) 이용 방식 필터
  const filteredByUsageMethod = useMemo(() => {
    if (usageMethod === "all") return filteredByCategory;
    return filteredByCategory.filter((p) =>
      p.usage_methods?.some((m) => m.method_type === usageMethod && m.enabled),
    );
  }, [filteredByCategory, usageMethod]);

  // 3) 가격 범위 필터
  const filteredByPrice = useMemo(() => {
    if (!priceRange) return filteredByUsageMethod;
    const [min, max] = priceRange;
    return filteredByUsageMethod.filter((p) => {
      const price = p.price_per_day || 0;
      return price >= min && price <= max;
    });
  }, [filteredByUsageMethod, priceRange]);

  // 4) 정렬 — 미선택(null)이면 서버의 거리+priority 순서 유지
  const sortedProducts = useMemo(() => {
    if (!sortBy) return filteredByPrice;
    const items = [...filteredByPrice];
    switch (sortBy) {
      case "price_low":
        return items.sort(
          (a, b) => (a.price_per_day || 0) - (b.price_per_day || 0),
        );
      case "price_high":
        return items.sort(
          (a, b) => (b.price_per_day || 0) - (a.price_per_day || 0),
        );
      case "newest":
        return items.sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
        );
      default:
        return items;
    }
  }, [filteredByPrice, sortBy]);

  // 빠른 카테고리 칩 개수 — 다른 필터와 무관하게 전체 목록 기준
  const quickChips = useMemo(
    () =>
      QUICK_CATEGORY_CHIPS.map((chip) => ({
        ...chip,
        count: products.filter((p) => isInCategory(p.category_code, chip.code))
          .length,
      })).filter((chip) => chip.count > 0),
    [products],
  );

  // 날짜 기반 예약 가능 여부
  const unavailableMap = useProductAvailability(
    sortedProducts,
    checkinDate,
    checkoutDate,
  );

  // 가격 슬라이더 최대값
  const maxProductPrice = useMemo(() => {
    const prices = products
      .map((p) => p.price_per_day || 0)
      .filter((p) => p > 0);
    return prices.length > 0 ? Math.max(...prices) : 500000;
  }, [products]);

  const activeFilterCount =
    selectedCategories.length +
    (usageMethod !== "all" ? 1 : 0) +
    (priceRange ? 1 : 0);

  // URL 쿼리 파라미터 동기화 (pushState로 깜빡임 없이 갱신)
  const updateUrlParams = useCallback(
    (updates: {
      checkin?: string | null;
      checkout?: string | null;
      categories?: string[];
      usageMethod?: UsageMethod;
      minPrice?: number | null;
      maxPrice?: number | null;
      sort?: SortOption;
    }) => {
      const params = new URLSearchParams();

      const ci = updates.checkin !== undefined ? updates.checkin : checkinDate;
      const co =
        updates.checkout !== undefined ? updates.checkout : checkoutDate;
      if (ci) params.set("checkin", ci);
      if (co) params.set("checkout", co);

      const cats = updates.categories ?? selectedCategories;
      if (cats.length > 0) params.set("categories", cats.join(","));

      const method = updates.usageMethod ?? usageMethod;
      if (method !== "all") params.set("usage_method", method);

      const minP =
        updates.minPrice !== undefined
          ? updates.minPrice
          : priceRange?.[0] ?? null;
      const maxP =
        updates.maxPrice !== undefined
          ? updates.maxPrice
          : priceRange?.[1] ?? null;
      if (minP !== null && minP > 0) params.set("min_price", String(minP));
      if (maxP !== null && maxP < maxProductPrice)
        params.set("max_price", String(maxP));

      const s = updates.sort ?? sortBy;
      if (s) params.set("sort", s);

      const qs = params.toString();
      window.history.pushState({}, "", `${pathname}${qs ? `?${qs}` : ""}`);
    },
    [
      checkinDate,
      checkoutDate,
      selectedCategories,
      usageMethod,
      priceRange,
      sortBy,
      maxProductPrice,
      pathname,
    ],
  );

  const handleDateChange = (
    checkin: string | null,
    checkout: string | null,
  ) => {
    setCheckinDate(checkin);
    setCheckoutDate(checkout);
    updateUrlParams({ checkin, checkout });
  };

  // 필터 드로어는 이용 방식·가격만 편집한다(카테고리는 CategorySheet 로 분리됨).
  const handleFilterApply = (
    method: UsageMethod,
    price: [number, number] | null,
  ) => {
    setUsageMethod(method);
    setPriceRange(price);
    updateUrlParams({
      usageMethod: method,
      minPrice: price?.[0] ?? null,
      maxPrice: price?.[1] ?? null,
    });
  };

  const handleQuickCategory = (codes: string[]) => {
    setSelectedCategories(codes);
    updateUrlParams({ categories: codes });
  };

  const handleSortChange = (sort: SortOption) => {
    setSortBy(sort);
    updateUrlParams({ sort });
  };

  return (
    <>
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">
        {heading}
      </h2>

      {/* 3버튼 필터 바 (날짜/필터/정렬) — navbar 아래 sticky. 섹션의 가로 패딩
          (px-4 sm:px-6 lg:px-0)을 negative margin으로 상쇄해 흰 배경이 컬럼
          전폭을 덮고, 내부 px-4(모바일)/래퍼 px(sm+)로 그리드와 정렬 유지 */}
      <div className="sticky top-[calc(57px+var(--loc-safe-area-inset-top))] md:top-[calc(81px+var(--loc-safe-area-inset-top))] z-40 bg-white -mx-4 sm:-mx-6 lg:mx-0 sm:px-6 lg:px-0 mb-3 sm:mb-4">
        <SearchFilterBar
          locale={locale}
          checkinDate={checkinDate}
          checkoutDate={checkoutDate}
          onDateChange={handleDateChange}
          activeFilterCount={activeFilterCount}
          onFilterDialogOpen={() => setIsFilterDialogOpen(true)}
          isSortActive={sortBy !== null}
          onSortDialogOpen={() => setIsSortDialogOpen(true)}
          hideMap={true}
          inDrawer={true}
          t={tSearch}
        />
      </div>

      {quickChips.length > 0 && (
        <div
          role="group"
          aria-label={tConcert("venue.categoryChipsLabel")}
          className="-mx-4 mb-4 flex gap-2 overflow-x-auto px-4 sm:mx-0 sm:px-0"
        >
          {[
            {
              key: "all",
              label: tConcert("venue.chipAll"),
              count: products.length,
              codes: [] as string[],
              active: selectedCategories.length === 0,
            },
            ...quickChips.map((chip) => ({
              key: chip.code,
              label: tConcert(chip.labelKey),
              count: chip.count,
              codes: [chip.code],
              active:
                selectedCategories.length === 1 &&
                selectedCategories[0] === chip.code,
            })),
          ].map((chip) => (
            <button
              key={chip.key}
              type="button"
              aria-pressed={chip.active}
              onClick={() => handleQuickCategory(chip.codes)}
              className={cn(
                "inline-flex h-8.5 shrink-0 items-center gap-1 whitespace-nowrap rounded-full border px-3 text-[13px] font-medium transition-colors",
                chip.active
                  ? "border-gray-900 bg-gray-900 text-white"
                  : "border-gray-200 bg-white text-gray-700 hover:border-gray-400",
              )}
            >
              {chip.label}
              <span
                className={cn(
                  "text-xs",
                  chip.active ? "text-gray-300" : "text-gray-500",
                )}
              >
                {chip.count}
              </span>
            </button>
          ))}
        </div>
      )}

      {sortedProducts.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5">
          {sortedProducts.map((product) => (
            <RealProductCard
              key={product.id}
              product={product}
              isUnavailable={unavailableMap.get(product.id)}
            />
          ))}
        </div>
      ) : (
        <div className="rounded-2xl border border-dashed border-gray-300 bg-gray-50/50 p-10 text-center">
          <p className="text-sm sm:text-base text-gray-600">{noResultsText}</p>
        </div>
      )}

      {/* 카테고리/이용방식/가격 필터 다이얼로그 */}
      <CategoryFilterDialog
        open={isFilterDialogOpen}
        onClose={() => setIsFilterDialogOpen(false)}
        allCategories={allCategories}
        selectedCategories={selectedCategories}
        onApply={handleFilterApply}
        usageMethod={usageMethod}
        onUsageMethodChange={setUsageMethod}
        priceRange={priceRange}
        maxProductPrice={maxProductPrice}
      />

      {/* 정렬 다이얼로그 */}
      <SortDialog
        open={isSortDialogOpen}
        onClose={() => setIsSortDialogOpen(false)}
        sortBy={sortBy}
        onSortChange={handleSortChange}
      />
    </>
  );
}
